import React, { useState, useMemo } from 'react';
import { Plus, Calendar, Package, Trash2, CheckCircle, XCircle, Download, Filter } from 'lucide-react';
import Card, { CardHeader } from '../components/ui/Card';
import Button from '../components/ui/Button';
import { useAppStore } from '../store';
import { format, addDays, isAfter, isBefore, parseISO } from 'date-fns';
import ExpressSampleModal from '../components/modals/ExpressSampleModal';
import EntityModal from '../components/modals/EntityModal';
import ConfirmModal from '../components/modals/ConfirmModal';
import ErrorModal from '../components/modals/ErrorModal';
import Select from '../components/ui/Select';
import { useAuth } from '../context/AuthContext';

const ExpressSamples: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [selectedSample, setSelectedSample] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [sampleToDelete, setSampleToDelete] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  
  // State for column clear confirmation
  const [clearColumnConfirmOpen, setClearColumnConfirmOpen] = useState(false);
  const [columnToClear, setColumnToClear] = useState<string | null>(null);
  
  // Filters
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [salespersonFilter, setSalespersonFilter] = useState<string>('all');
  const [deliveryDateFilter, setDeliveryDateFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  
  const { expressSamples, expressSampleItems, clients, salespersons, addClient, deleteExpressSample, updateExpressSample } = useAppStore();
  const { isAdmin } = useAuth();

  // Apply filters to express samples
  const filteredSamples = useMemo(() => {
    return expressSamples.filter(sample => {
      // Filter by client
      if (clientFilter !== 'all' && sample.client_id !== clientFilter) {
        return false;
      }

      // Filter by salesperson
      if (salespersonFilter !== 'all' && sample.salesperson !== salespersonFilter) {
        return false;
      }

      // Filter by type (liso/estampado)
      if (typeFilter !== 'all') {
        const isPrinted = typeFilter === 'printed';
        if (sample.is_printed !== isPrinted) {
          return false;
        }
      }

      // Filter by delivery date
      if (deliveryDateFilter !== 'all') {
        const today = new Date();
        const deliveryDate = parseISO(sample.delivery_date);
        
        switch (deliveryDateFilter) {
          case '2days':
            return isAfter(deliveryDate, today) && 
                   isBefore(deliveryDate, addDays(today, 2));
          case '7days':
            return isAfter(deliveryDate, today) && 
                   isBefore(deliveryDate, addDays(today, 7));
          case '15days':
            return isAfter(deliveryDate, today) && 
                   isBefore(deliveryDate, addDays(today, 15));
          default:
            return true;
        }
      }

      return true;
    });
  }, [expressSamples, clientFilter, salespersonFilter, deliveryDateFilter, typeFilter]);

  // Group samples by status
  const samplesByStatus = {
    pending: filteredSamples.filter(sample => sample.status === 'pending'),
    in_progress: filteredSamples.filter(sample => sample.status === 'in_progress'),
    finished: filteredSamples.filter(sample => sample.status === 'finished'),
    shipped: filteredSamples.filter(sample => sample.status === 'shipped'),
  };

  const getClientName = (clientId: string) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || 'Cliente no encontrado';
  };

  const getSampleItems = (sampleId: string) => {
    return expressSampleItems.filter(item => item.express_sample_id === sampleId);
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, sampleId: string) => {
    e.dataTransfer.setData('sampleId', sampleId);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>, newStatus: 'pending' | 'in_progress' | 'finished' | 'shipped') => {
    e.preventDefault();
    const sampleId = e.dataTransfer.getData('sampleId');
    
    try {
      await updateExpressSample(sampleId, { status: newStatus });
    } catch (error: any) {
      console.error('Error updating sample status:', error);
      setError(error.message || 'Error al actualizar el estado de la muestra');
      setShowErrorModal(true);
    }
  };

  const handleDelete = async () => {
    if (!sampleToDelete) return;
    try {
      await deleteExpressSample(sampleToDelete);
      setDeleteConfirmOpen(false);
      setSampleToDelete(null);
    } catch (error: any) {
      console.error('Error deleting sample:', error);
      setError(error.message || 'Error al eliminar la muestra');
      setShowErrorModal(true);
    }
  };

  const handleEditSample = (sampleId: string) => {
    setSelectedSample(sampleId);
    setIsModalOpen(true);
  };

  // Reset all filters
  const resetFilters = () => {
    setClientFilter('all');
    setSalespersonFilter('all');
    setDeliveryDateFilter('all');
    setTypeFilter('all');
  };

  // Function to render DTF status indicators
  const renderDtfStatus = (sample: typeof expressSamples[0]) => {
    return (
      <div className="flex flex-col gap-1 mt-2">
        {sample.dtf_printed && (
          <div className="flex items-center text-xs">
            <span className="mr-1">DTF enviado:</span>
            {sample.dtf_printed === 'Sí' ? (
              <CheckCircle className="h-3.5 w-3.5 text-green-500" />
            ) : (
              <XCircle className="h-3.5 w-3.5 text-red-500" />
            )}
          </div>
        )}
        
        {sample.dtf_picked_up && (
          <div className="flex items-center text-xs">
            <span className="mr-1">DTF retirado:</span>
            {sample.dtf_picked_up === 'Sí' ? (
              <CheckCircle className="h-3.5 w-3.5 text-green-500" />
            ) : (
              <XCircle className="h-3.5 w-3.5 text-red-500" />
            )}
          </div>
        )}
      </div>
    );
  };

  // Handle clear column
  const handleClearColumn = (status: string) => {
    setColumnToClear(status);
    setClearColumnConfirmOpen(true);
  };

  // Confirm clear column
  const confirmClearColumn = async () => {
    if (!columnToClear) return;
    
    try {
      // Get all sample IDs for the selected status
      const sampleIds = filteredSamples
        .filter(sample => sample.status === columnToClear)
        .map(sample => sample.id);
      
      // Delete each sample
      for (const id of sampleIds) {
        await deleteExpressSample(id);
      }
      
      setClearColumnConfirmOpen(false);
      setColumnToClear(null);
    } catch (error: any) {
      console.error('Error clearing column:', error);
      setError(error.message || 'Error al eliminar las muestras');
      setShowErrorModal(true);
    }
  };

  const renderSample = (sample: typeof expressSamples[0]) => {
    const items = getSampleItems(sample.id);
    const totalQty = items.reduce((sum, itm) => sum + itm.quantity, 0);
    
    // Keep the card background white regardless of is_printed
    const cardBgClass = 'bg-white dark:bg-gray-800';
    
    return (
      <div
        key={sample.id}
        draggable
        onDragStart={(e) => handleDragStart(e, sample.id)}
        onClick={() => handleEditSample(sample.id)}
        className={`${cardBgClass} p-4 rounded-lg shadow-sm cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all duration-200 relative group select-none border border-transparent hover:border-blue-200 dark:hover:border-blue-700`}
        title="Click para editar o arrastrar para mover"
      >
        {/* Delete button */}
        <button
          onClick={(e) => {
            e.stopPropagation(); // Prevent opening edit modal
            setSampleToDelete(sample.id);
            setDeleteConfirmOpen(true);
          }}
          className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <Trash2 className="h-4 w-4" />
        </button>

        <h4 className="font-medium text-gray-900 dark:text-white">{sample.name}</h4>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{getClientName(sample.client_id)}</p>
        <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mt-2">
          <Calendar className="h-4 w-4 mr-1" />
          {format(new Date(sample.delivery_date), 'dd/MM/yyyy')}
        </div>
        
        {/* Type indicator */}
        <div className="mt-2 text-xs">
          <span className={`px-2 py-1 rounded-full ${
            sample.is_printed 
              ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200' 
              : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
          }`}>
            {sample.is_printed ? 'Estampado' : 'Liso'}
          </span>
        </div>
        
        {/* DTF Status */}
        {(sample.dtf_printed || sample.dtf_picked_up) && renderDtfStatus(sample)}
        
        {/* Shipping Label */}
        {sample.shipping_label && (
          <div className="flex items-center mt-2">
            <a
              href={sample.shipping_label}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center text-xs text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
            >
              <Download className="h-3 w-3 mr-1" />
              Etiqueta de envío
            </a>
          </div>
        )}
        
        {/* Items Summary */}
          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
              <Package className="h-4 w-4 mr-1" />
              <span>{totalQty} unidades</span>
            </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Muestras Express</h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            icon={<Filter className="h-4 w-4" />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filtros
          </Button>
          <Button
            variant="primary"
            icon={<Plus className="h-5 w-5" />}
            onClick={() => {
              // NUEVA muestra = limpiar todo lo anterior
              localStorage.removeItem('express_sample_approved_file');
              localStorage.removeItem('express_sample_dtf_file');
              localStorage.removeItem('express_sample_payment_receipt');
              localStorage.removeItem('express_sample_shipping_label');
              localStorage.removeItem('express_sample_form_data');
              localStorage.removeItem('express_sample_items');
              localStorage.removeItem('express_sample_file_names');

              setSelectedSample(null); // Esto asegura que es NUEVA muestra
              setIsModalOpen(true);
            }}
          >
            Agregar Muestra
          </Button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <div className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="w-40">
                <Select
                  label="Cliente"
                  value={clientFilter}
                  onChange={setClientFilter}
                  options={[
                    { value: 'all', label: 'Todos' },
                    ...clients.map(client => ({
                      value: client.id,
                      label: client.name
                    }))
                  ]}
                />
              </div>
              
              <div className="w-40">
                <Select
                  label="Vendedor"
                  value={salespersonFilter}
                  onChange={setSalespersonFilter}
                  options={[
                    { value: 'all', label: 'Todos' },
                    ...salespersons.map(sp => ({
                      value: sp.name,
                      label: sp.name
                    }))
                  ]}
                />
              </div>
              
              <div className="w-40">
                <Select
                  label="Entrega"
                  value={deliveryDateFilter}
                  onChange={setDeliveryDateFilter}
                  options={[
                    { value: 'all', label: 'Todas' },
                    { value: '2days', label: 'Próx. 2 días' },
                    { value: '7days', label: 'Próx. 7 días' },
                    { value: '15days', label: 'Próx. 15 días' }
                  ]}
                />
              </div>
              
              <div className="w-40">
                <Select
                  label="Tipo"
                  value={typeFilter}
                  onChange={setTypeFilter}
                  options={[
                    { value: 'all', label: 'Todos' },
                    { value: 'plain', label: 'Liso' },
                    { value: 'printed', label: 'Estampado' }
                  ]}
                />
              </div>
              
              <div className="flex items-center">
                <Button
                  variant="outline"
                  icon={<Filter className="h-4 w-4" />}
                  onClick={resetFilters}
                  className="h-12 px-3"
                >
                  Limpiar Filtros
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card>
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Pending Column */}
            <div 
              className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, 'pending')}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  Pendiente <span className="font-normal text-sm">({samplesByStatus.pending.length})</span>
                </h3>
                {isAdmin && samplesByStatus.pending.length > 0 && (
                  <button
                    onClick={() => handleClearColumn('pending')}
                    className="text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                    title="Eliminar todas las muestras pendientes"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {samplesByStatus.pending.map(renderSample)}
                {samplesByStatus.pending.length === 0 && (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    No hay muestras pendientes
                  </div>
                )}
              </div>
            </div>

            {/* In Progress Column */}
            <div 
              className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, 'in_progress')}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  En Proceso <span className="font-normal text-sm">({samplesByStatus.in_progress.length})</span>
                </h3>
                {isAdmin && samplesByStatus.in_progress.length > 0 && (
                  <button
                    onClick={() => handleClearColumn('in_progress')}
                    className="text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                    title="Eliminar todas las muestras en proceso"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {samplesByStatus.in_progress.map(renderSample)}
                {samplesByStatus.in_progress.length === 0 && (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    No hay muestras en proceso
                  </div>
                )}
              </div>
            </div>

            {/* Finished Column */}
            <div 
              className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, 'finished')}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  Terminado <span className="font-normal text-sm">({samplesByStatus.finished.length})</span>
                </h3>
                {isAdmin && samplesByStatus.finished.length > 0 && (
                  <button
                    onClick={() => handleClearColumn('finished')}
                    className="text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                    title="Eliminar todas las muestras terminadas"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {samplesByStatus.finished.map(renderSample)}
                {samplesByStatus.finished.length === 0 && (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    No hay muestras terminadas
                  </div>
                )}
              </div>
            </div>

            {/* Shipped Column */}
            <div 
              className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, 'shipped')}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  Despachado <span className="font-normal text-sm">({samplesByStatus.shipped.length})</span>
                </h3>
                {isAdmin && samplesByStatus.shipped.length > 0 && (
                  <button
                    onClick={() => handleClearColumn('shipped')}
                    className="text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                    title="Eliminar todas las muestras despachadas"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {samplesByStatus.shipped.map(renderSample)}
                {samplesByStatus.shipped.length === 0 && (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    No hay muestras despachadas
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>

      <ExpressSampleModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedSample(null);
        }}
        sampleId={selectedSample}
      />

      <EntityModal
        isOpen={isClientModalOpen}
        onClose={() => setIsClientModalOpen(false)}
        entityType="client"
        onSave={async (client) => {
          try {
            await addClient(client);
            setIsClientModalOpen(false);
          } catch (error: any) {
            console.error('Error creating client:', error);
            setError(error.message || 'Error al crear el cliente');
            setShowErrorModal(true);
          }
        }}
      />

      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setSampleToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Eliminar Muestra"
        message={
          <div>
            <p>¿Estás seguro de que deseas eliminar esta muestra? Esta acción no se puede deshacer.</p>
            <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-md">
              <p className="text-amber-800 dark:text-amber-200">⚠️ Eliminar esta muestra no devuelve los ítems al stock. Solamente si corresponde, deberás reponerlos manualmente en la sección de stock.</p>
            </div>
          </div>
        }
        confirmText="Eliminar"
        variant="danger"
      />

      <ConfirmModal
        isOpen={clearColumnConfirmOpen}
        onClose={() => {
          setClearColumnConfirmOpen(false);
          setColumnToClear(null);
        }}
        onConfirm={confirmClearColumn}
        title={`Eliminar todas las muestras ${
          columnToClear === 'pending' ? 'pendientes' :
          columnToClear === 'in_progress' ? 'en proceso' :
          columnToClear === 'finished' ? 'terminadas' : 'despachadas'
        }`}
        message={
          <div>
            <p>{`¿Estás seguro de que deseas eliminar todas las muestras ${
              columnToClear === 'pending' ? 'pendientes' :
              columnToClear === 'in_progress' ? 'en proceso' :
              columnToClear === 'finished' ? 'terminadas' : 'despachadas'
            }? Esta acción no se puede deshacer.`}</p>
            <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-md">
              <p className="text-amber-800 dark:text-amber-200">⚠️ Eliminar estas muestras no devuelve los ítems al stock. Solamente si corresponde, deberás reponerlos manualmente en la sección de stock.</p>
            </div>
          </div>
        }
        confirmText="Eliminar todas"
        variant="danger"
      />

      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={error || 'Ha ocurrido un error'}
      />
    </div>
  );
};

export default ExpressSamples;